import React, { useState, useEffect } from "react";
import { Row, Col, Button, InputGroup, Form } from "react-bootstrap";
import { ActionHandlerFunction, AdminApprovalGroup, AdminUser } from "@preveil-api";
import {
  Account, AdminUIActionTypes, AdminErrorMessages, account_types, dayjs, Message, MessageHandlerDisplayType, useAppDispatch,
  useAppSelector, AdminMessages, MessageAnchors, MessageToastTypes, HEADERS, BOM
} from "src/common";
import { Icon } from "src/components";
import { AddUsersModal, ResendInvitesModal, SetRecoveryGroupModal } from "../toolbar-actions";
import { uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import saveAs from "file-saver";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  is_loading: boolean;
  users_list: AdminUser[];
  handleAction: ActionHandlerFunction;
  filter_param?: string;
  recovery_groups: AdminApprovalGroup[];
}

function UsersToolbarComponent(props: AllProps) {
  const { is_loading, users_list, filter_param, handleAction, recovery_groups, current_account } = props;
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state: RootState) => state.admin.selected);
  const accounts = useAppSelector((state: RootState) => state.account.accounts);
  const [send_invites_disabled, setSendInvitesDisabled] = useState<boolean>(false);
  const [recovery_group_message, setRecoveryGroupMessage] = useState<string>("Set Recovery Group For Selected Accounts");
  const [show_add_users_modal, setShowAddUsersModal] = useState<boolean>(false);
  const [show_resend_invites_modal, setShowResendInvitesModal] = useState<boolean>(false);
  const [show_set_recovery_group_modal, setShowSetRecoveryGroupModal] = useState<boolean>(false);
  const [selected_users, setSelectedUsers] = useState<AdminUser[]>([]);
  const [pending_users, setPendingUsers] = useState<AdminUser[]>([]);
  const [local_users_accounts_map, setLocalUsersAccountsMap] = useState<Map<string, Account>>();

  useEffect(() => {
    const users = users_list.filter((user) => selected.includes(user.user_id));
    setSelectedUsers(users);
    const pending_users = users.filter((user) => user.status === "pending");
    setPendingUsers(pending_users);
    const contains_pending = pending_users.length === 0;
    setSendInvitesDisabled(contains_pending);
    contains_pending && setRecoveryGroupMessage("Recovery Groups cannot be set on Pending Accounts");
  }, [selected]);

  useEffect(() => {
    if (accounts && accounts.length >= 1) {
      const all_accounts_map = new Map(_.map(accounts, (account: Account) => {
        const key = account.user_id;
        return [key, account];
      }));

      setLocalUsersAccountsMap(all_accounts_map);
    }
  }, [accounts]);

  // Description: CSV Export for the users list.
  function handleExportUsersList() {
    try {
      const csv_string = [
        [...HEADERS],
        ...users_list.map((user: AdminUser) => [
          user.display_name,
          user.user_id,
          user.account_type === account_types.full ? "Full Account User" : user.account_type === account_types.express ? "Express User" : "Pending",
          user.status,
          user.department,
          user.role,
          !!user.recovery_group ? user.recovery_group.name : "Not Assigned"
        ])
      ]
        .map(row => row.join(","))
        .join("\n");

      const csv_data = `${BOM}${csv_string}`;
      const blob = new Blob([csv_data], { type: "text/csv; charset=utf-8" });
      saveAs(blob, `admin-users-${dayjs(new Date()).format("MM.DD.YYYY-HH_mm_ss")}.csv`);
    } catch (error) {
      dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_admin_users_export_csv, MessageHandlerDisplayType.toastr), error));
    }
  };

  // Description: Confirm dialog for deleting selected users.
  function confirmDelete() {
    const selected_user = users_list.find((user) => selected.includes(user.user_id));
    const confirmation_dialog = new Message(
      AdminMessages.confirm_delete_admin_users.body.replace(
        MessageAnchors.message_content,
        selected.length > 1
          ? `these <b>${selected.length}</b> accounts?`
          : !!selected_user ? `user <b>${selected_user.display_name}'s</b> (<b>${selected_user.user_id}</b>)?` : ""
      ),

      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      AdminMessages.confirm_delete_admin_users.title,
      {
        label: "Yes",
        data: true,
        action: () => handleAction({ actionType: AdminUIActionTypes.Delete })
      },
      {
        label: "No"
      }
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  // Description: Action Modals for Add Users, Resend Invites and Set Recovery Group.
  function toolbarModals() {
    return <>
      <AddUsersModal
        current_account={current_account}
        org_users={users_list}
        show_modal={show_add_users_modal}
        setShowModal={setShowAddUsersModal}
        local_users_accounts_map={local_users_accounts_map}
        handleAction={handleAction}
      />

      <ResendInvitesModal
        current_account={current_account}
        show_modal={show_resend_invites_modal}
        setShowModal={setShowResendInvitesModal}
        handleAction={handleAction}
        users={pending_users}
      />

      <SetRecoveryGroupModal
        current_account={current_account}
        show_modal={show_set_recovery_group_modal}
        setShowModal={setShowSetRecoveryGroupModal}
        recovery_groups={recovery_groups}
        handleAction={handleAction}
        selected_users={selected_users}
        users_list={users_list}
      />
    </>;
  }

  return <Row className="toolbar admin">
    <Col xs="auto" className="ps-2 ms-1 col-sm-4">
      <InputGroup size="sm">
        <InputGroup.Text>
          <Icon className="ficon-filter" />
        </InputGroup.Text>
        <Form.Control
          id="filter_tb"
          disabled={users_list.length === 0}
          value={filter_param}
          onChange={(e) =>
            handleAction({
              actionType: AdminUIActionTypes.SearchParam,
              params: e.target.value,
            })
          }
          type="text"
          placeholder="Filter..."
          aria-label="Filter"
        />
        {
          !!filter_param && filter_param.length > 0 &&
          <Button
            variant="icon"
            size="sm"
            className="input-group-btn"
            onClick={() => {
              handleAction({ actionType: AdminUIActionTypes.SearchParam });
            }} >
            <Icon className="ficon-x-circle" />
          </Button>
        }
      </InputGroup>
    </Col>
    <Col className="order-1">
      <Button
        className="ms-1 action-toolbar-buttons"
        variant="icon"
        size="sm"
        onClick={() => setShowAddUsersModal(true)}
        title="Add Users"
        data-tooltip-id="pv-tooltip"
        data-tooltip-content="Add Users"
      >
        <Icon className="ficon-plus" />
      </Button>
      <Button
        className="action-toolbar-buttons"
        variant="icon"
        size="sm"
        onClick={() => confirmDelete()}
        disabled={selected.length === 0}
        title="Delete Selected"
        data-tooltip-id="pv-tooltip"
        data-tooltip-content="Delete Selected"
      >
        <Icon className="ficon-trash-2" />
      </Button>
      <Button
        variant="icon"
        size="sm"
        onClick={() => handleExportUsersList()}
        title="Export Users List"
        data-tooltip-id="pv-tooltip"
        data-tooltip-content="Export Users List"
      >
        <Icon className="ficon-download" />
      </Button>
      <Button
        variant="icon"
        size="sm"
        disabled={selected.length === 0}
        onClick={() => setShowSetRecoveryGroupModal(true)}
        title="Set Recovery Group"
        data-tooltip-id="pv-tooltip"
        data-tooltip-content={recovery_group_message}
      >
        <Icon className="ficon-users" />
      </Button>
      <Button
        variant="icon"
        size="sm"
        disabled={send_invites_disabled || selected.length === 0}
        onClick={() => setShowResendInvitesModal(true)}
        title="Resend Invite"
        data-tooltip-id="pv-tooltip"
        data-tooltip-content="To resend invitations, please select users who are listed as pending."
      >
        <Icon className="ficon-mail" />
      </Button>
    </Col>
    <Col xs="auto" className="order-sm-3 order-4 me-2 pagination_info">
      {users_list.length > 0 && `${users_list.length} User${users_list.length > 1 ? "s" : ""}`}
    </Col>
    <Col className="divider order-sm-3 order-1"></Col>
    <Col xs="auto" className="p-0 order-sm-3 order-4 ms-1 refresh" >
      <Button variant="icon" size="sm"
        onClick={() => handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest })}
        title="Refresh Users">
        {
          is_loading ?
            <i className="spinner"></i> :
            <Icon className="ficon-rotate-cw" />
        }
      </Button>
    </Col>
    {toolbarModals()}
  </Row >;
}

export default React.memo(UsersToolbarComponent);